import { absoluteValue } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { AssetNotFoundException } from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { asterAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new Error('apiSecret is required for Aster')
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  // Futures収益履歴を取得
  const incomes = await client.run({
    method: 'GET',
    path: '/fapi/v1/income',
    query: {
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
      limit: '1000',
    },
    responseSchema,
  })

  // 時系列順にソート
  const sortedIncomes = incomes.sort((a, b) => a.time - b.time)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const income of sortedIncomes) {
    fetchedFrom =
      fetchedFrom === undefined
        ? income.time
        : Math.min(fetchedFrom, income.time)
    fetchedTo =
      fetchedTo === undefined ? income.time : Math.max(fetchedTo, income.time)

    // 金額が0の場合はスキップ
    if (income.income.value === 0n) {
      continue
    }

    const asset = getAsset(income.asset)
    if (!asset) {
      throw new AssetNotFoundException(income.asset)
    }

    // incomeTypeに応じてActionタイプを決定
    let actionType: Action['type']
    let comment: string

    switch (income.incomeType) {
      case 'FUNDING_FEE':
        actionType =
          income.income.value > 0n ? 'derivative-profit' : 'derivative-fee'
        comment = `Funding Fee: ${income.symbol}`
        break
      case 'COMMISSION':
        actionType = income.income.value > 0n ? 'income' : 'fee'
        comment = `Commission: ${income.symbol}`
        break
      case 'TRANSFER':
        actionType = 'ignore'
        comment = `Transfer: ${income.info}`
        break
      case 'REALIZED_PNL':
        actionType =
          income.income.value > 0n ? 'realize-profit' : 'realize-loss'
        comment = `Realized PnL: ${income.symbol}`
        break
      case 'INSURANCE_CLEAR':
        actionType = income.income.value > 0n ? 'income' : 'fee'
        comment = `Insurance Clear: ${income.symbol}`
        break
      case 'REFERRAL_KICKBACK':
        actionType = 'income'
        comment = 'Referral Kickback'
        break
      case 'COMMISSION_REBATE':
        actionType = 'income'
        comment = 'Commission Rebate'
        break
      default:
        // その他の場合は収入か手数料として扱う
        actionType = income.income.value > 0n ? 'income' : 'fee'
        comment = `${income.incomeType}: ${income.symbol || income.info}`
        break
    }

    actions.push({
      comment,
      evidence: 'system-rule',
      order: 0,
      source: `${asterAccountCategory}_${config.type}_${income.tranId}`,
      type: actionType,
      timestamp: income.time,
      transfers: [
        {
          asset,
          amount: absoluteValue(income.income),
          direction: income.income.value > 0n ? 'in' : 'out',
        },
      ],
    })
  }

  // Sort actions by timestamp in ascending order
  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: incomes.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
